import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ClipboardList, Loader2, Send } from 'lucide-react';
import type { TaskDTO } from '@prism/contracts';
import { api, errorMessage } from '../api/client';
import { useAssignees, useTasks } from '../api/hooks';
import { Modal } from './Modal';
import { toast } from './Toasts';
import { queryKeys } from '../api/queryKeys';

type TaskSubject = { type: 'asset' | 'sequence' | 'collection'; id: string; name: string };

/** datetime-local value (local time) ↔ ISO. */
export const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/** "person:<id>", "role:<key>" (1.0.0-rc.9) or "group:<id>" from a task's assignee. */
export const assigneeValue = (a: TaskDTO['assignee']) => (a ? `${a.type === 'user' ? 'person' : a.type}:${a.id}` : '');
export const assigneeBody = (v: string) => ({
  assigneeUserId: v.startsWith('person:') ? v.slice(7) : null,
  assigneeGroupId: v.startsWith('group:') ? v.slice(6) : null,
  assigneeRoleKey: v.startsWith('role:') ? v.slice(5) : null,
});

/** People, roles and access groups (a role's or group's task is every member's; one of them can take it). */
export function AssigneeSelect({ value, onChange, id = 'task-assignee' }: { value: string; onChange: (v: string) => void; id?: string }) {
  const a = useAssignees().data;
  return (
    <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value)} data-testid="task-assignee">
      <option value="">Nobody yet</option>
      <optgroup label="People">{a?.people.map((p) => <option key={p.id} value={`person:${p.id}`}>{p.name}</option>)}</optgroup>
      {a && a.roles.length > 0 && <optgroup label="Roles">{a.roles.map((r) => <option key={r.key} value={`role:${r.key}`}>{r.name} ({r.members})</option>)}</optgroup>}
      {a && a.groups.length > 0 && <optgroup label="Groups">{a.groups.map((g) => <option key={g.id} value={`group:${g.id}`}>{g.name} ({g.members})</option>)}</optgroup>}
    </select>
  );
}

export function NewTaskDialog({ subject, onClose }: { subject: TaskSubject | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(subject ? `Review ${subject.name}` : '');
  const [details, setDetails] = useState('');
  const [assignee, setAssignee] = useState('');
  const [due, setDue] = useState('');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api('/api/v1/tasks', {
        method: 'POST',
        json: { title: title.trim(), details, subject: subject ? { type: subject.type, id: subject.id } : null, ...assigneeBody(assignee), dueAt: due ? new Date(due).toISOString() : null },
      });
      await qc.invalidateQueries({ queryKey: queryKeys.tasksAll() });
      toast('Task assigned');
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title={subject ? `Assign “${subject.name}”` : 'New task'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={busy || !title.trim()} onClick={() => void save()} data-testid="save-task">{busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Assign</button>
    </>}>
      <div className="field"><label htmlFor="task-title">Task</label><input id="task-title" className="input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="task-title" /></div>
      <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="field"><label htmlFor="task-assignee">Assign to</label><AssigneeSelect value={assignee} onChange={setAssignee} /></div>
        <div className="field"><label htmlFor="task-due">Deadline</label><input id="task-due" className="input" type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-due" /></div>
      </div>
      <div className="field"><label htmlFor="task-details">Details and notes</label>
        <textarea id="task-details" className="input" rows={5} value={details} onChange={(e) => setDetails(e.target.value)} placeholder="What needs doing, and anything the assignee should know" data-testid="task-details" /></div>
      {subject?.type === 'collection' && <p className="faint" style={{ fontSize: 12, margin: 0 }}>Your collections are private; the assignee can open this one from the task while it is open.</p>}
    </Modal>
  );
}

/** "Assign" on an asset, an edit or a collection, with a link to its open tasks. */
export function AssignButton({ subject }: { subject: TaskSubject }) {
  const [open, setOpen] = useState(false);
  const q = { assetId: subject.type === 'asset' ? subject.id : undefined, sequenceId: subject.type === 'sequence' ? subject.id : undefined, collectionId: subject.type === 'collection' ? subject.id : undefined, status: 'active' as const };
  const tasks = useTasks(q).data?.items ?? [];
  return (
    <>
      <button className="btn sm" onClick={() => setOpen(true)} data-testid="assign-task"><ClipboardList size={14} /> Assign</button>
      {tasks.length > 0 && (
        <Link className="pill" to={`/tasks?scope=all&${subject.type}=${subject.id}`} title={tasks.map((t) => `${t.title}${t.assignee ? ` → ${t.assignee.name}` : ''}`).join('\n')} data-testid="subject-tasks">
          {tasks.length} open task{tasks.length === 1 ? '' : 's'}{tasks.some((t) => t.overdue) ? ' · overdue' : ''}
        </Link>
      )}
      {open && <NewTaskDialog subject={subject} onClose={() => setOpen(false)} />}
    </>
  );
}
